/**
 * Filters Component
 * Report filtering controls with Alpine.js
 */

import Alpine from 'alpinejs'
import type { ReportStatus, AnimalType } from '../types'

// ============================================================================
// Types
// ============================================================================

export interface FilterOptions {
  showStatus?: boolean
  showAnimalType?: boolean
  showLocation?: boolean
  showDateRange?: boolean
  defaultStatus?: ReportStatus | 'all'
  defaultAnimalType?: AnimalType | 'all'
}

// ============================================================================
// Status Filter Options
// ============================================================================

export const STATUS_OPTIONS: { value: ReportStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending Approval' },
  { value: 'new', label: 'New' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
]

export const ANIMAL_TYPE_OPTIONS: { value: AnimalType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Animals' },
  { value: 'dog', label: 'Dogs' },
  { value: 'cat', label: 'Cats' },
  { value: 'bird', label: 'Birds' },
  { value: 'other', label: 'Other' },
]

export const DATE_RANGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
]

// ============================================================================
// Filters Component
// ============================================================================

/**
 * Create a filters component
 */
export function filtersComponent(options: FilterOptions = {}) {
  return {
    // Filter values
    status: options.defaultStatus ?? 'all',
    animalType: options.defaultAnimalType ?? 'all',
    country: 'all',
    city: 'all',
    dateRange: 'all',

    // UI state
    showFilters: false,
    loading: false,

    // Options
    statusOptions: STATUS_OPTIONS,
    animalTypeOptions: ANIMAL_TYPE_OPTIONS,
    dateRangeOptions: DATE_RANGE_OPTIONS,
    countries: [] as string[],
    cities: [] as string[],

    // Configuration
    showStatus: options.showStatus ?? true,
    showAnimalType: options.showAnimalType ?? true,
    showLocation: options.showLocation ?? true,
    showDateRange: options.showDateRange ?? true,

    /**
     * Initialize filters and load data
     */
    async init(): Promise<void> {
      if (this.showLocation) {
        await this.loadCountries()
      }
    },

    /**
     * Toggle filter panel visibility
     */
    toggleFilters(): void {
      this.showFilters = !this.showFilters
    },

    /**
     * Apply filters to reports store
     */
    applyFilters(): void {
      const store = Alpine.store('reports') as {
        setFilter(key: string, value: string): void
      }

      if (this.showStatus) {
        store.setFilter('status', this.status)
      }
      if (this.showAnimalType) {
        store.setFilter('animalType', this.animalType)
      }
      if (this.showLocation) {
        store.setFilter('country', this.country)
        store.setFilter('city', this.city)
      }
      if (this.showDateRange) {
        store.setFilter('dateRange', this.dateRange)
      }
    },

    /**
     * Reset all filters
     */
    resetFilters(): void {
      this.status = 'all'
      this.animalType = 'all'
      this.country = 'all'
      this.city = 'all'
      this.dateRange = 'all'
      this.cities = []

      const store = Alpine.store('reports') as { clearFilters(): void }
      store.clearFilters()
    },

    /**
     * Handle status change
     */
    onStatusChange(): void {
      this.applyFilters()
    },

    /**
     * Handle animal type change
     */
    onAnimalTypeChange(): void {
      this.applyFilters()
    },

    /**
     * Handle country change
     */
    async onCountryChange(): Promise<void> {
      this.city = 'all'

      if (this.country !== 'all') {
        await this.loadCities(this.country)
      } else {
        this.cities = []
      }

      this.applyFilters()
    },

    /**
     * Handle city change
     */
    onCityChange(): void {
      this.applyFilters()
    },

    /**
     * Handle date range change
     */
    onDateRangeChange(): void {
      this.applyFilters()
    },

    /**
     * Load countries from store
     */
    async loadCountries(): Promise<void> {
      const store = Alpine.store('reports') as {
        loadCountries(): Promise<void>
        countries: string[]
      }

      await store.loadCountries()
      this.countries = store.countries
    },

    /**
     * Load cities for a country
     */
    async loadCities(country: string): Promise<void> {
      const store = Alpine.store('reports') as {
        loadCities(country: string): Promise<void>
        cities: string[]
      }

      await store.loadCities(country)
      this.cities = store.cities
    },

    /**
     * Check if any filters are active
     */
    get hasActiveFilters(): boolean {
      return (
        this.status !== 'all' ||
        this.animalType !== 'all' ||
        this.country !== 'all' ||
        this.city !== 'all' ||
        this.dateRange !== 'all'
      )
    },

    /**
     * Get count of active filters
     */
    get activeFilterCount(): number {
      let count = 0
      if (this.status !== 'all') {
        count++
      }
      if (this.animalType !== 'all') {
        count++
      }
      if (this.country !== 'all') {
        count++
      }
      if (this.city !== 'all') {
        count++
      }
      if (this.dateRange !== 'all') {
        count++
      }
      return count
    },
  }
}

// ============================================================================
// Quick Filter Component
// ============================================================================

/**
 * Quick filter pills for common filters
 */
export function quickFilters() {
  return {
    activeFilter: 'all',

    // Sprite IDs match `/assets/icons.svg#i-…`. Per decision D2 the "All" pill
    // is text-only (no `i-apps` exists in the sprite); D4 maps `priority_high`
    // → `i-error`; D5 maps `near_me` → `i-my-location`.
    filters: [
      { value: 'all', label: 'All', icon: '' },
      { value: 'dogs', label: 'Dogs', icon: 'i-pets' },
      { value: 'cats', label: 'Cats', icon: 'i-pets' },
      { value: 'urgent', label: 'Urgent', icon: 'i-error' },
      { value: 'nearby', label: 'Nearby', icon: 'i-my-location' },
    ],

    /**
     * Select a quick filter
     */
    select(filter: string): void {
      this.activeFilter = filter

      const store = Alpine.store('reports') as {
        setFilter(key: string, value: string): void
        fetchNearby(lat: number, lng: number, radiusKm: number): Promise<void>
      }

      switch (filter) {
        case 'dogs':
          store.setFilter('animalType', 'dog')
          break
        case 'cats':
          store.setFilter('animalType', 'cat')
          break
        case 'urgent':
          store.setFilter('status', 'new')
          break
        case 'nearby':
          void this.filterNearby()
          break
        default:
          store.setFilter('animalType', 'all')
          store.setFilter('status', 'all')
      }
    },

    /**
     * Filter by nearby location
     */
    async filterNearby(): Promise<void> {
      if (!navigator.geolocation) {
        console.warn('Geolocation not supported')
        return
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const store = Alpine.store('reports') as {
            fetchNearby(lat: number, lng: number, radiusKm: number): Promise<void>
          }

          await store.fetchNearby(
            position.coords.latitude,
            position.coords.longitude,
            10 // 10km radius
          )
        },
        (error) => {
          console.error('Geolocation error:', error)
        }
      )
    },

    /**
     * Check if a filter is active
     */
    isActive(filter: string): boolean {
      return this.activeFilter === filter
    },
  }
}

// ============================================================================
// Register Components
// ============================================================================

export function registerFilterComponents(): void {
  Alpine.data('filters', filtersComponent)
  Alpine.data('quickFilters', quickFilters)
}
